/**
 * Support-request server functions. Server-only.
 *
 * `submitSupportMessage` is the app's only PUBLIC (unauthenticated) write, so it
 * carries its own protections rather than relying on any route guard:
 *  - the payload is validated + length-capped (lib/validation.ts),
 *  - a per-IP window caps submissions (lib/server/ip-rate-limit.ts),
 *  - honeypot / dwell failures are DROPPED SILENTLY — the caller gets the same
 *    success it would get for a real submission, so a bot author learns nothing
 *    about which check fired.
 *
 * Account context (`userId`, `organizationId`) is read from the SESSION and
 * never from the payload. That is the substantive advantage over the `mailto:`
 * links this replaced: the org attribution cannot be edited or removed by the
 * sender.
 *
 * The remaining fns are admin-only and only ever write triage state (status,
 * internal note) — a support message body is immutable once received.
 */
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { desc, eq, inArray } from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import { organization, user as userTable } from '@/db/auth-schema'
import { organizationBilling, supportMessages } from '@/db/schema'
import { requirePlatformAdmin } from '@/lib/admin/require-admin'
import { PublicError } from '@/lib/errors'
import { getPlatformAdminRecipients } from '@/lib/org/owners.server'
import { auth } from '@/lib/server/auth'
import { sendEmail } from '@/lib/server/email'
import { renderSupportRequestEmail } from '@/lib/server/emails/support-request'
import { env } from '@/lib/server/env'
import { consumeRateLimit } from '@/lib/server/ip-rate-limit'
import {
  resolveSupportIdentity,
  type SupportIdentity,
} from '@/lib/support/identity.server'
import {
  looksAutomated,
  SUPPORT_RATE_LIMIT,
  type SupportMessageStatus,
  type SupportTopic,
  supportSubmissionSchema,
} from '@/lib/support/support'
import {
  emitBusinessEvent,
  resolveClientIp,
} from '@/lib/telemetry/business.server'
import { optionalNote, parseInput, id as zId } from '@/lib/validation'

/** A row as the admin page renders it. */
export interface SupportMessageRow {
  id: string
  topic: SupportTopic
  name: string
  email: string
  message: string
  status: SupportMessageStatus
  userId: string | null
  organizationId: string | null
  orgName: string | null
  orgSlug: string | null
  pagePath: string | null
  clientIp: string | null
  internalNote: string | null
  handledByName: string | null
  handledAt: Date | null
  createdAt: Date
}

/**
 * Account context lines for the alert email — resolved server-side from the
 * session, so support sees who really sent this regardless of what was typed in
 * the name field.
 */
async function accountContextFor(
  userId: string | null,
  organizationId: string | null,
): Promise<string[]> {
  const lines: string[] = []
  if (userId) {
    const [row] = await db
      .select({ email: userTable.email, name: userTable.name })
      .from(userTable)
      .where(eq(userTable.id, userId))
      .limit(1)
    if (row) lines.push(`${row.name} <${row.email}>`)
  }
  if (organizationId) {
    const [row] = await db
      .select({
        name: organization.name,
        slug: organization.slug,
        planSlug: organizationBilling.planSlug,
        billingMode: organizationBilling.billingMode,
      })
      .from(organization)
      .leftJoin(
        organizationBilling,
        eq(organizationBilling.organizationId, organization.id),
      )
      .where(eq(organization.id, organizationId))
      .limit(1)
    if (row) {
      lines.push(`org ${row.name} (${row.slug})`)
      lines.push(
        `plan ${row.planSlug ?? 'none'} / ${row.billingMode ?? 'none'}`,
      )
    }
  }
  return lines
}

/**
 * Record an inbound support request and announce it internally. Public: callable
 * without a session.
 *
 * Always resolves `{ ok: true }` on anything that is not a hard failure —
 * including a silently-dropped bot submission — so the form's success state
 * never doubles as a spam oracle. A tripped rate limit is the one case that
 * surfaces, because a real person resending deserves to be told why.
 */
export const submitSupportMessage = createServerFn({ method: 'POST' })
  .validator((d: unknown) => parseInput(supportSubmissionSchema, d))
  .handler(async ({ data }) => {
    // Advisory client signals: drop, don't reject.
    if (looksAutomated(data)) return { ok: true as const }

    const clientIp = resolveClientIp()
    const decision = await consumeRateLimit({
      scope: 'support-submit',
      identifier: clientIp,
      max: SUPPORT_RATE_LIMIT.max,
      windowSeconds: SUPPORT_RATE_LIMIT.windowSeconds,
    })
    if (!decision.allowed) {
      throw new PublicError(
        'Too many messages from this network. Try again later, or email support@masklabs.io.',
        429,
      )
    }

    const { headers } = getRequest()
    const session = await auth.api.getSession({ headers })
    const userId = session?.user.id ?? null
    const organizationId = session?.session.activeOrganizationId ?? null

    const [inserted] = await db
      .insert(supportMessages)
      .values({
        topic: data.topic,
        // Signed-in submitters get the session's identity, not the form's: the
        // fields are hidden in that case, and trusting the payload would let a
        // logged-in user file a request under someone else's address.
        name: session?.user.name ?? data.name,
        email: session?.user.email ?? data.email,
        message: data.message,
        userId,
        organizationId,
        pagePath: data.pagePath ?? null,
        clientIp: clientIp === 'unknown' ? null : clientIp,
      })
      .returning({ id: supportMessages.id })

    if (!inserted) throw new Error('support message insert returned no row')

    emitBusinessEvent('support.request', {
      message_id: inserted.id,
      topic: data.topic,
      user_id: userId ?? undefined,
      org_id: organizationId ?? undefined,
      signed_in: Boolean(session),
    })

    // Alert delivery must not decide whether the request was accepted: the row
    // is already committed, and a dropped SMTP call is recoverable from the
    // admin page. sendEmail never throws (see its module doc).
    const accountLines = await accountContextFor(userId, organizationId)
    const rendered = await renderSupportRequestEmail({
      topic: data.topic,
      name: session?.user.name ?? data.name,
      email: session?.user.email ?? data.email,
      message: data.message,
      accountLines,
      adminUrl: `${env.BETTER_AUTH_URL}/admin/support`,
    })
    const recipients = env.SUPPORT_ALERT_EMAIL
      ? [env.SUPPORT_ALERT_EMAIL]
      : (await getPlatformAdminRecipients()).map((a) => a.email)
    for (const to of recipients) {
      await sendEmail({
        to,
        ...rendered,
        // The whole reply flow: answering in the shared mailbox reaches the
        // customer, so the app never needs to send on support's behalf.
        replyTo: session?.user.email ?? data.email,
        type: 'support-request',
      })
    }

    return { ok: true as const }
  })

/** Every support message, newest first. Admin-only. */
export const listSupportMessages = createServerFn({ method: 'GET' }).handler(
  async (): Promise<SupportMessageRow[]> => {
    await requirePlatformAdmin()
    const rows = await db
      .select({
        id: supportMessages.id,
        topic: supportMessages.topic,
        name: supportMessages.name,
        email: supportMessages.email,
        message: supportMessages.message,
        status: supportMessages.status,
        userId: supportMessages.userId,
        organizationId: supportMessages.organizationId,
        orgName: organization.name,
        orgSlug: organization.slug,
        pagePath: supportMessages.pagePath,
        clientIp: supportMessages.clientIp,
        internalNote: supportMessages.internalNote,
        handledByUserId: supportMessages.handledByUserId,
        handledAt: supportMessages.handledAt,
        createdAt: supportMessages.createdAt,
      })
      .from(supportMessages)
      .leftJoin(
        organization,
        eq(organization.id, supportMessages.organizationId),
      )
      .orderBy(desc(supportMessages.createdAt))
      .limit(500)

    // Resolve the handling admin's display name in one follow-up query rather
    // than a self-join on `user` (drizzle needs an alias for that, and this
    // set is tiny — one row per admin who has ever triaged something).
    const handlerIds = [
      ...new Set(rows.map((r) => r.handledByUserId).filter((v) => v !== null)),
    ]
    const handlers =
      handlerIds.length > 0
        ? await db
            .select({ id: userTable.id, name: userTable.name })
            .from(userTable)
            .where(inArray(userTable.id, handlerIds))
        : []
    const handlerName = new Map(handlers.map((h) => [h.id, h.name]))

    return rows.map(({ handledByUserId, ...row }) => ({
      ...row,
      handledByName: handledByUserId
        ? (handlerName.get(handledByUserId) ?? null)
        : null,
    }))
  },
)

/** Flip a message between new and handled. Admin-only. */
export const setSupportMessageStatus = createServerFn({ method: 'POST' })
  .validator((d: { id: string; status: SupportMessageStatus }) =>
    parseInput(z.object({ id: zId, status: z.enum(['new', 'handled']) }), d),
  )
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const handled = data.status === 'handled'
    const [updated] = await db
      .update(supportMessages)
      .set({
        status: data.status,
        // Reopening clears the stamp, so "handled by" always describes the
        // CURRENT state rather than a stale earlier pass.
        handledByUserId: handled ? caller.userId : null,
        handledAt: handled ? new Date() : null,
      })
      .where(eq(supportMessages.id, data.id))
      .returning({ id: supportMessages.id })
    if (!updated) throw new PublicError('Message not found', 404)
    return { ok: true as const }
  })

/** Set (or clear) the admin-only note on a message. Admin-only. */
export const setSupportMessageNote = createServerFn({ method: 'POST' })
  .validator((d: { id: string; note: string }) =>
    parseInput(z.object({ id: zId, note: optionalNote(2000) }), d),
  )
  .handler(async ({ data }) => {
    await requirePlatformAdmin()
    const [updated] = await db
      .update(supportMessages)
      .set({ internalNote: data.note })
      .where(eq(supportMessages.id, data.id))
      .returning({ id: supportMessages.id })
    if (!updated) throw new PublicError('Message not found', 404)
    return { ok: true as const }
  })

/**
 * Signed identity for the PostHog Support widget, for the CALLER's own session
 * (see support/identity.server.ts). `null` when signed out or unconfigured, in
 * which case the widget keeps its anonymous, browser-bound tickets.
 */
export const getSupportIdentity = createServerFn({ method: 'GET' }).handler(
  (): Promise<SupportIdentity | null> => resolveSupportIdentity(),
)
